import React, { ReactElement, useState, useEffect, useRef } from 'react';
import Box from '../../components/Box/Box';
import CategoryMenuBarItem from './CategoryMenuBarItem';
import Icon from '../../components/Icon/Icon';
import BasicModal from '../../components/Modal/BasicModal';
import getComponentClassNames from '../../utils/className/getComponentClassNames';
import { TCategoryItem } from '../../types/TCategoryItem';
import { isEnter, isEscape } from '../../utils/keyboard';

const classNames = getComponentClassNames(
  ['CategoryMenuBar'],
  [
    'expanded',
    'topLevel',
    'topLevelItems',
    'expandToggle',
    'expandToggleLabel',
    'expandToggleIcon',
    'background',
    'foldout',
    'list',
    'listItem',
    'noScroll',
    'modalContent',
  ],
);

type TCategoryMenuBarProps = {
  children?: ReactElement;
  topCategories: TCategoryItem[];
  l1Categories: TCategoryItem[];
  toggleLabel: string;
  className?: string;
};

type TKeyboardEvent = KeyboardEvent | React.KeyboardEvent<HTMLDivElement>;

function getNextIndex(focusableElements: NodeListOf<HTMLElement>, event: TKeyboardEvent) {
  // Find the currently focused element
  const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
  const direction = event.shiftKey ? -1 : 1; // Use shift key to reverse direction
  return (currentIndex + direction + focusableElements.length) % focusableElements.length;
}

function getFocusableElements(overlayRef: React.RefObject<HTMLDivElement>): NodeListOf<HTMLElement> {
  return (overlayRef.current?.querySelectorAll<HTMLElement>(
    'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
  ) ?? []) as NodeListOf<HTMLElement>;
}

function hasFocusableElements(focusableElements: NodeListOf<HTMLElement> | undefined) {
  return focusableElements && focusableElements.length > 0;
}

const CategoryMenuBar = ({ topCategories, l1Categories, toggleLabel, ...rest }: TCategoryMenuBarProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isLargeMobile, setIsLargeMobile] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);
  const topCategoriesRef = useRef<HTMLDivElement>(null);
  const expandToggleRef = useRef<HTMLDivElement>(null);

  const handleFocusOut = (event: FocusEvent) => {
    if (overlayRef.current && !overlayRef.current.contains(event.relatedTarget as Node)) {
      setExpanded(false);
    }
  };

  const escapeListener = (event: TKeyboardEvent) => {
    if (isEscape(event.key) && isExpanded) {
      expandToggleRef.current?.focus(); // Return focus to the toggle button
      setExpanded(false);
    }
  };

  const enterListener = (event: TKeyboardEvent) => {
    if (isEnter(event.key)) setExpanded(!isExpanded);
  };

  const tabListener = (event: TKeyboardEvent) => {
    if (!isExpanded || event.key !== 'Tab') return;

    event.preventDefault(); // Prevent default tab behavior

    const overlayElements = getFocusableElements(overlayRef);
    const topCategoryElements = getFocusableElements(topCategoriesRef);

    if (!hasFocusableElements(overlayElements) || !hasFocusableElements(topCategoryElements)) return;

    // Handle navigation from overlay to top categories (Shift+Tab from first overlay element)
    if (document.activeElement === overlayElements[0] && event.shiftKey) {
      topCategoryElements[0]?.focus();
      return;
    }

    // Handle navigation from first top category element to last overlay element (Shift+Tab from first top category element)
    if (document.activeElement === topCategoryElements[0] && event.shiftKey) {
      overlayElements[overlayElements.length - 1]?.focus();
      return;
    }

    // Handle navigation from last overlay element to second top category element (Tab from last overlay element)
    if (document.activeElement === overlayElements[overlayElements.length - 1] && !event.shiftKey) {
      topCategoryElements[1]?.focus();
      return;
    }

    overlayElements[getNextIndex(overlayElements, event)]?.focus();
  };

  useEffect(() => {
    const largeMobile = getComputedStyle(document.documentElement).getPropertyValue('--is-large-mobile') === '1';
    setIsLargeMobile(largeMobile);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escapeListener, false);
    document.addEventListener('keydown', tabListener, false);
    return () => {
      document.removeEventListener('keydown', escapeListener, false);
      document.removeEventListener('keydown', tabListener, false);
    };
  });

  useEffect(() => {
    document.body.classList.toggle(classNames.noScroll, isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    const overlayElement = overlayRef.current;
    if (overlayElement) {
      overlayElement.addEventListener('focusout', handleFocusOut, false);
    }

    return () => {
      if (overlayElement) {
        overlayElement.removeEventListener('focusout', handleFocusOut, false);
      }
    };
  }, [isExpanded]);

  const CategoryList = () => (
    <ul className={classNames.list}>
      {l1Categories.map(({ id, name, url, trackingAttribute, icon, badgeLabel }) => (
        <li key={id} className={classNames.listItem}>
          <CategoryMenuBarItem
            key={name}
            href={url}
            icon={icon || id}
            label={name}
            attr={trackingAttribute}
            badgeLabel={badgeLabel}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <Box
      {...rest}
      as="nav"
      name="CategoryMenuBar"
      className={isExpanded ? classNames.expanded : ''}
      role="navigation"
      ref={topCategoriesRef}
    >
      <div className={classNames.topLevel}>
        <div className={classNames.topLevelItems}>
          <div
            className={classNames.expandToggle}
            onClick={() => setExpanded(!isExpanded)}
            onKeyDown={enterListener}
            aria-haspopup="true"
            aria-expanded={isExpanded}
            tabIndex={0}
            role="button"
            ref={expandToggleRef}
          >
            <span className={classNames.expandToggleLabel}>{toggleLabel}</span>
            <Icon name="arrow-down" className={classNames.expandToggleIcon} />
          </div>
          {topCategories.map(({ id, name, url, trackingAttribute, icon, badgeLabel }) => (
            <CategoryMenuBarItem
              href={url}
              key={name}
              icon={icon || id}
              label={name}
              attr={trackingAttribute}
              badgeLabel={badgeLabel}
            />
          ))}
        </div>
      </div>
      {isExpanded && isLargeMobile && (
        <>
          <div
            className={classNames.background}
            onClick={() => setExpanded(false)}
            onKeyDown={escapeListener}
            tabIndex={-1}
          />
          <div className={classNames.foldout} aria-label="submenu" ref={overlayRef}>
            <CategoryList />
          </div>
        </>
      )}
      {isExpanded && !isLargeMobile && (
        <BasicModal
          title={toggleLabel}
          onClose={() => setExpanded(false)}
          contentClassName={classNames.modalContent}
          isBottomSheet={false}
        >
          <CategoryList />
        </BasicModal>
      )}
    </Box>
  );
};

export default CategoryMenuBar;
